/**
 * Feature copy, English.
 *
 * Written to sell, which here means: lead with what the practice gets, and keep the specific fact
 * as the proof behind it. "Two people at reception cannot book the same chair" rather than
 * "double-booking is prevented by the database itself" — same guarantee, addressed to the person
 * who has to care about it.
 *
 * ⚠️ Benefit-first is not the same as vague. "Streamline your workflow" would be worse than the
 * technical version it replaced, because the specificity is what makes any of this believable. If a
 * line cannot name the thing that makes it true, it is not ready.
 *
 * Ground rules, because copy that overclaims costs more than thin copy — it turns a trial into a
 * complaint:
 *   · every specific here was checked against the implementation while writing
 *   · nothing about telemedicine or anything else unbuilt
 *   · features that depend on an account elsewhere say so in `needs`, never as a silent promise
 *   · gaps go in `roadmap` and are votable; deliberate choices go in `boundaries` and are not
 *     apologies. Putting a decision under "coming soon" promises to undo it
 */
export const featuresEn = {
  indexTitle: 'What easyDent does for your practice',
  indexSubtitle:
    'Every feature, what it changes about your day, and plainly where it stops. '
    + 'Written for someone deciding whether this fits their practice — not for a brochure.',
  groups: {
    day: 'Running the day',
    clinical: 'Clinical',
    money: 'Money',
    growth: 'Patients & growth',
    team: 'Team & practice',
    platform: 'Platform',
  },
  backToIndex: 'All features',
  howItWorks: 'How it works',
  whatItDoes: 'What it does',
  whoFor: 'Who it is for',
  boundaries: 'Where it stops, on purpose',
  needs: 'What you need for this',
  comingSoon: 'Coming soon',
  comingSoonBlurb: 'Not built yet. Tell us which of these matters to you — it is how we decide what to build next.',
  wantThis: 'I want this',
  wanted: 'You asked for this',
  votes_one: '{{count}} person asked for this',
  votes_other: '{{count}} people asked for this',
  votesNone: 'Nobody has asked for this yet',
  notifyTitle: 'Want telling when one of these ships?',
  notifyPlaceholder: 'your@email.com',
  notifySubmit: 'Notify me',
  notifyConsent: 'Email me about easyDent updates. I can withdraw at any time using the link in any message.',
  notifyConsentRequired: 'Please tick the box so we may email you.',
  notifyDone: 'Done — we will let you know.',
  notifyError: 'That did not go through. Please check the address and try again.',
  notifyPrivacy: 'We store your address and nothing else, use it only for this, and never pass it on.',
  related: 'Related',
  readMore: 'Read more →',
  tryDemo: 'See it in the demo',
  signIn: 'Sign in',
  ctaTitle: 'Easier to see than to read about',
  ctaBody: 'The demo clinic is a real, working practice with patients, appointments and records already in it. Nothing to install, no card.',
  ctaButton: 'Open the demo clinic',

  // ── Running the day ────────────────────────────────────────────────────────
  scheduling: {
    title: 'Scheduling',
    summary: 'One calendar the whole team can work in at once, without ever double-booking a chair.',
    steps: [
      { title: 'Set it up once', body: 'Your chairs, your opening hours, which doctor works where. Half an hour, and you only do it once.' },
      { title: 'Book from anywhere', body: 'Reception books, or the patient books themselves online. The slot is as long as the treatment needs.' },
      { title: 'The day runs itself', body: 'Confirmations and reminders go out on their own. You watch the board and treat patients.' },
    ],
    details: [
      'Two people at reception cannot book the same chair. The second one is told immediately, while the patient is still standing there.',
      'Slots are as long as the treatment actually takes, so the day does not quietly overrun by eleven o’clock.',
      'Patients cancel and reschedule from the link in their reminder — no phone call, no login, and the slot goes back on offer at once.',
      'See one chair, one doctor, or every location at the same time if you run more than one.',
      'Appointments likely to be missed are flagged, so the extra reminder goes to the patients who need it rather than to everyone.',
      'A practice in more than one city keeps the right time in each. Nobody arrives an hour early.',
    ],
    audience: 'Any practice with more than one chair — and especially one running several locations off a single front desk.',
    roadmap: {
      'recurring': 'Recurring appointment series — booking a course of visits in one go.',
      'resources': 'Rooms and equipment as separately bookable resources, alongside chairs and doctors.',
    },
  },
  'patient-records': {
    title: 'Patient records',
    summary: 'Everything about a patient in one place, so nobody has to ask them the same question twice.',
    steps: [
      { title: 'Add them in seconds', body: 'A name and a phone number is enough to book. The rest can wait until they are in the chair.' },
      { title: 'Get the history before they arrive', body: 'Send a link and the medical history is answered at home — you read it before they sit down.' },
      { title: 'Everything gathers here', body: 'Appointments, treatments, payments, X-rays and documents all land on the same record by themselves.' },
    ],
    details: [
      'A patient without an email is a normal patient, not a workaround. The phone number is the identifier.',
      'Allergies, current medication and chronic conditions are proper fields you can see at a glance — not a sentence buried in last year’s notes.',
      'The medical history arrives before the appointment does, so the first five minutes are not spent on paperwork.',
      'Romanian CNP is checked as it is typed, so a wrong digit is caught before it reaches an invoice.',
      'Contact details and national ID are encrypted, and you can still search on them exactly as before.',
      'Likely duplicate patients are found overnight and put in front of a human. Nothing is ever merged behind your back.',
    ],
    audience: 'Every practice. It is the spine everything else hangs from.',
    roadmap: {
      'import': 'Bringing your patients across from your current software, so nobody retypes ten years of records.',
    },
    boundaries: [
      'Duplicate patients are flagged for a human to decide on. Nothing is merged automatically — an incorrect merge is far harder to undo than a duplicate is to live with.',
    ],
  },
  'waiting-list': {
    title: 'Waiting list',
    summary: 'A cancellation stops being lost money. The gap gets offered to someone who wanted in sooner.',
    steps: [
      { title: 'Note who wants sooner', body: 'A patient who cannot wait for the next free slot goes on the list, with what they need doing.' },
      { title: 'Somebody cancels', body: 'The gap is offered straight away to the patient it actually fits — right treatment, right length.' },
      { title: 'They take it', body: 'One tap on a link and the chair is filled. No account, no phone tag, no empty hour.' },
    ],
    details: [
      'A late cancellation gets filled while it is still worth filling, instead of becoming an hour nobody is paid for.',
      'The offer goes to a patient the slot actually suits, so you are not fitting an hour of work into twenty minutes.',
      'Patients claim by tapping a link — the ones who have never logged in anywhere can still take the slot.',
      'Offers go out in order rather than to everybody at once, so you are not fielding six calls about one slot.',
      'The list clears itself after 30 days, so it does not quietly fill with people who were treated months ago.',
    ],
    audience: 'Practices with a real waiting time, where a cancellation the day before is money you never see.',
    roadmap: {
      'cross-branch': 'Offering a patient a free slot at one of your other locations, not just the one they asked for.',
      'priority': 'Automatic priority scoring, so the list is not purely first-come.',
    },
  },

  // ── Clinical ───────────────────────────────────────────────────────────────
  'dental-chart': {
    title: 'Dental chart',
    summary: 'The whole mouth on one screen, and the story of every tooth behind it.',
    steps: [
      { title: 'Open the chart', body: 'Teeth drawn as teeth, adult or paediatric — not a spreadsheet of numbers to decode.' },
      { title: 'Tap what you see', body: 'Set a surface, add a note against the tooth. It takes about as long as saying it out loud.' },
      { title: 'Look back whenever', body: 'Today’s chart is on screen; the whole history is one click away when a patient asks how it got here.' },
    ],
    details: [
      'You can answer "when did this start?" without opening a paper file, because every change to every tooth is kept.',
      'Five surfaces per tooth, so what you record is what you actually saw — not "upper left, roughly".',
      'Built for a tablet at the chair, so you chart while you look at the patient instead of afterwards from memory.',
      'Ten conditions covering what you actually meet: caries, filled, crowned, extracted, implant, bridge abutment, root canal, missing and impacted.',
      'Notes appear as callouts on the tooth they belong to, so the next clinician sees them without hunting.',
      'Nothing is overwritten. A colleague’s entry from three years ago is still exactly what they wrote.',
    ],
    audience: 'Any dentist who charts — and especially practices where several clinicians treat the same patient over years.',
    roadmap: {
      'doctor-colours': 'Each doctor’s work in their own colour on the chart, so you can see at a glance who did what.',
      'perio': 'Periodontal charting — pocket depths, bleeding and recession indices.',
    },
  },
  consultations: {
    title: 'Consultations & treatment plans',
    summary: 'Record the visit once, and the chart, the quote and the bill all follow from it.',
    steps: [
      { title: 'Open it from the calendar', body: 'One click from the appointment. No hunting for the right patient.' },
      { title: 'Record the work', body: 'Pick treatments from your own catalogue, per tooth. Prices come with them and can be changed.' },
      { title: 'Quote or bill', body: 'Mark work planned or done. A treatment offer prints on your letterhead for the patient to take away.' },
    ],
    details: [
      'Recording the work updates the tooth chart, moves the stock it used, and adds to the bill — one act, not four.',
      'A quote and a bill can never be confused, because planned and completed work are counted separately.',
      'The patient owes what was actually done, never what was merely suggested.',
      'Your catalogue, your prices, your VAT rates and your durations — not a preloaded list you have to work around.',
      'A treatment offer leaves with the patient as a PDF on your letterhead, which is a far better answer than "I will email you".',
      'What each procedure earns you, after materials and commission, comes out of the same recording — see Reports.',
    ],
    audience: 'Practices that quote before treating, and anyone tired of entering the same visit into three places.',
    roadmap: {
      'esign': 'Treatment plans signed electronically by the patient, instead of a PDF you print or send.',
    },
    boundaries: [
      'The catalogue holds a price. What a procedure costs to deliver is worked out in the profitability report, from the bill of materials, the doctor’s commission and chair time.',
    ],
  },
  imaging: {
    title: 'Imaging & DICOM',
    summary: 'X-rays open in the browser, on the patient’s record, on any machine in the building.',
    steps: [
      { title: 'Upload', body: 'Drag the files onto the patient. Big studies go straight to storage without tying up the app.' },
      { title: 'Open it anywhere', body: 'Any computer with a browser. Nothing to install, nothing to license per workstation.' },
      { title: 'Work with the image', body: 'Window and level, zoom, pan, measure, annotate.' },
    ],
    details: [
      'No viewer to install on every machine, and nothing to reinstall when a computer is replaced.',
      'A colleague in another room opens the same study without anyone copying a file onto a stick.',
      'Measure and annotate in the browser, so what you noticed is on the study rather than in your head.',
      'X-rays, photographs and documents sit on the same record — you are not looking in two systems for one patient.',
      'Images are private: each viewing uses a link that expires in minutes, and the files never sit on a shared drive.',
      'Keep the files in your own storage if you would rather — see Security & your data.',
    ],
    audience: 'Practices with intraoral or panoramic imaging, and anyone tired of installing a viewer on every workstation.',
    roadmap: {
      'cbct': '3D and CBCT reconstruction. Studies open as 2D series today.',
      'direct-capture': 'Direct capture from imaging hardware, rather than uploading files after acquisition.',
    },
  },
  'imaging-referrals': {
    title: 'Imaging referrals',
    summary: 'Send a patient for an X-ray and get the study back on their record, without chasing anyone.',
    steps: [
      { title: 'Choose your centres', body: 'The imaging centres you actually work with. Only they can receive your referrals.' },
      { title: 'Refer from the chair', body: 'Which views, which teeth, why. Takes less time than writing it on paper.' },
      { title: 'The study comes back', body: 'It lands on the patient record and on the consultation you referred from. Nobody carries a CD.' },
    ],
    details: [
      'You stop phoning the centre to ask whether the patient turned up — the referral says where they are.',
      'If a patient never arrives, you find out, which is the only way to chase them before the problem gets worse.',
      'The centre can decline with a reason, so you can send the patient somewhere else the same day.',
      'The study returns onto the record and the consultation, so it is where you look for it rather than in an inbox.',
      'The centre sees the patient’s name, date of birth, phone and why you referred. Not your records, and not your other patients.',
      'Results are marked as reviewed when a clinician opens them, so nothing sits unread for a fortnight.',
    ],
    audience: 'Practices without their own radiography — which in dentistry is most of them.',
    boundaries: [
      'Referrals go only to the centres you chose. There is no open marketplace — who gets to read a patient’s referral should be your decision, made in advance.',
      'The patient pays the centre directly. Payment for the study does not pass through easyDent, and we take nothing from it.',
    ],
  },

  // ── Money ──────────────────────────────────────────────────────────────────
  sterilization: {
    title: 'Sterilisation traceability',
    summary: 'Answer an inspector in a minute: this tray, this cycle, this patient, this date.',
    steps: [
      { title: 'Label your trays', body: 'Each tray gets a code. Print it as a QR or barcode and stick it on.' },
      { title: 'Record the cycle', body: 'Autoclave, programme, which trays were in it. Recording signs it in your name.' },
      { title: 'Scan at the chair', body: 'The tray is tied to the patient it was used on. The chain writes itself.' },
    ],
    details: [
      'An inspection becomes a report you print, over any period, instead of an afternoon with a folder.',
      'Before a tray goes near a patient, one word tells you whether it may be used.',
      'Every cycle carries the name of whoever ran it, so accountability is recorded as it happens rather than reconstructed later.',
      'Failed and interrupted cycles are kept, not quietly dropped — a log with no failures in it is the one nobody believes.',
      'A tray that has never been sterilised says so, instead of looking the same as one that has.',
      'Autoclaves carry their service date and warn you once it passes.',
    ],
    audience: 'Any practice that has to show an inspector which instruments touched which patient.',
    roadmap: {
      'labels': 'Generating printable labels and driving a label printer, rather than you printing the codes yourself.',
      'autoclave': 'Reading cycles from the autoclave’s own log, instead of the operator recording them.',
      'service-reminder': 'A reminder before an autoclave is due for service, not only a warning once it is overdue.',
    },
  },

  payments: {
    title: 'Payments & deposits',
    summary: 'Get paid at the desk, by link, or up front — and stop chasing people for it.',
    steps: [
      { title: 'At the desk', body: 'Cash or card terminal against the visit. The receipt goes out on its own.' },
      { title: 'Or by link', body: 'Send a pay link after they leave. They tap it and pay from the car park if they like.' },
      { title: 'Or before they come', body: 'Ask for a deposit at booking, so a no-show costs them something too.' },
    ],
    details: [
      'A deposit at booking turns a no-show from your loss into their decision.',
      'A pay link means the awkward conversation about last month’s balance mostly stops happening.',
      'Reception can see what a patient owes at checkout, so it gets collected while they are still at the desk.',
      'Money settles into your own account, not ours. We are not in the middle of it.',
      'A patient can never be charged twice for the same thing, whatever the card machine or the network does.',
      'Doctors see the clinical record without the money attached to it — the balance is a front-desk concern.',
    ],
    audience: 'Practices losing chairs to no-shows, and anyone tired of ringing patients about an old balance.',
    roadmap: {
      'fiscal-register': 'Fiscal cash register and till printer integration. Receipts are PDFs today.',
      'instalments': 'Payment plans and instalments, so a large treatment can be paid across several months.',
    },
    needs: [
      'Taking money online means signing up with the card processor first — a form and a bank account. Until then, everything at the desk works as normal.',
    ],
  },
  invoicing: {
    title: 'Invoicing & e-Factura',
    summary: 'e-Factura that goes through, and a way to prove it would before you commit to anything.',
    steps: [
      { title: 'Issue', body: 'The invoice comes from the work you already recorded. Numbering is sequential, per clinic.' },
      { title: 'Check it against ANAF', body: 'One button tells you whether ANAF would accept it — and exactly what it objects to if not.' },
      { title: 'File it', body: 'Once enrolled, invoices upload themselves and the outcome comes back without you watching.' },
    ],
    details: [
      'Press "check with ANAF" today, before enrolling anything — you can find out your invoices would be accepted without committing to us first.',
      'Rejections tell you which field is wrong, not just that something is. Nobody guesses.',
      'An invoice number can never be used twice, so a duplicate cannot appear during your fiscal inspection.',
      'Enrolment happens once, in your own browser, with your own certificate. It never reaches us, and afterwards it renews itself.',
      'Filed invoices are followed up until ANAF gives a verdict, and the signed archive is kept as your proof.',
      'Outside Romania you get a clean PDF invoice with the right VAT, emailed to the patient.',
    ],
    audience: 'Romanian practices that must file e-Factura, and EU practices that need a correct invoice without thinking about it.',
    roadmap: {
      'spv-inbound': 'Pulling your suppliers’ invoices in from e-Factura, so you never key one in again.',
      'fiscal-register': 'Fiscal cash register and till printer integration.',
    },
    needs: [
      'Filing needs your clinic enrolled with its own qualified certificate. Until then invoices are still numbered, produced and stored — only submission waits.',
    ],
  },
  commissions: {
    title: 'Doctor commissions',
    summary: 'What each associate is owed, worked out for you, defensible line by line.',
    steps: [
      { title: 'Agree it once', body: 'A percentage, a fixed amount per treatment, or both — per doctor, and per treatment where it differs.' },
      { title: 'It prices itself', body: 'Every completed treatment records the agreement that governed it, on the day it happened.' },
      { title: 'Pay from the statement', body: 'A statement per doctor per period, with the work behind every line.' },
    ],
    details: [
      'The end of the month stops being an evening with a spreadsheet.',
      'Agreeing a new percentage today cannot reprice last quarter — including months you have already paid. The rate that applies is the one that was in force when the work was done.',
      'A doctor querying a figure can be shown the treatments behind it, on the spot.',
      'Different rates for different treatments, not just different doctors — implants and check-ups need not share a percentage.',
      'What each procedure really earns is net of what it pays out, because commission feeds the profitability report.',
    ],
    audience: 'Practices paying associates a share of what they bring in rather than a salary.',
    roadmap: {
      'payroll': 'Running payroll and filing it — today this works out what is owed and stops there.',
      'split': 'Splitting one treatment’s commission between several doctors.',
      'targets': 'Targets and bonus tiers, so a rate can step up once a threshold is passed.',
    },
  },

  reports: {
    title: 'Reports',
    summary: 'What you earned, what each procedure actually makes, and who owes you.',
    steps: [
      { title: 'Pick a period', body: 'Any dates, one location or all of them.' },
      { title: 'See where the money is', body: 'Revenue over time, by location, by payment method — and margin per procedure.' },
      { title: 'Do something about it', body: 'The debtors list is a call list: who owes what, biggest first.' },
    ],
    details: [
      'Profit per procedure, after materials, the doctor’s commission and chair time — so you can tell which treatments are worth promoting and which are costing you.',
      'Margin per chair hour, which is the number that tells you what to put in the diary.',
      'The debtors list is a call list, not a report — largest outstanding first, with a phone number.',
      'Revenue by location, so a second surgery can be judged on its own figures.',
      'What messaging is costing you, by channel, so it never quietly becomes a line you cannot explain.',
      'Owners and managers see all of this. Doctors and reception do not.',
    ],
    audience: 'Owners and practice managers who want the real picture without exporting anything into a spreadsheet.',
    roadmap: {
      'utilisation': 'Per-doctor productivity and chair-utilisation analysis.',
      'forecasting': 'Forecasting — projecting revenue forward rather than reporting what happened.',
    },
    boundaries: [
      'Material cost comes from the materials you listed against the treatment, so one with no list is costed on commission and chair time alone.',
    ],
  },

  // ── Patients & growth ──────────────────────────────────────────────────────
  'patient-portal': {
    title: 'Patient portal',
    summary: 'Patients answer their own questions, so reception stops answering them.',
    steps: [
      { title: 'Invite them', body: 'One click from their record. They accept with a link — no password to invent at the desk.' },
      { title: 'They look after themselves', body: 'Appointments, treatments, chart, X-rays and balance, from their phone.' },
      { title: 'Fewer calls for you', body: '"When am I in?" and "can you send me my X-ray?" stop arriving by phone.' },
    ],
    details: [
      'The two calls reception fields most — when is my appointment, send me my X-ray — the patient can answer themselves at ten at night.',
      'They book, cancel and reschedule without occupying anyone at the front desk.',
      'They pay what they owe from the phone, which gets more of it paid.',
      'One account covers several clinics, so a patient of two of your locations is one person, not two registrations.',
      'They can only read their clinical record, never change it.',
      'It installs on a phone like an app, in Romanian or English.',
    ],
    audience: 'Practices fielding a stream of "when is my appointment" and "can you send me my X-ray" calls.',
    boundaries: [
      'An account exists only when a clinic invites the patient. There is no public sign-up — nobody should be able to claim a medical record by knowing an email address.',
      'The portal is read-only clinically. Patients see their record; they cannot edit it.',
    ],
  },
  communications: {
    title: 'Patient messaging',
    summary: 'Reminders that go out by themselves, in the patient’s language, on the channel they answer.',
    steps: [
      { title: 'Choose the channel', body: 'Each patient has one they actually read. Messages follow it, and try another if it fails.' },
      { title: 'Write it once', body: 'Templates per clinic and per language, with a preview before anything leaves.' },
      { title: 'Forget about it', body: 'Confirmations and 24-hour and 1-hour reminders send without anyone remembering to.' },
    ],
    details: [
      'Empty chairs from forgotten appointments largely stop, without anyone at reception phoning round.',
      'SMS, WhatsApp, email or web push — whichever that patient actually reads, with a fallback if it fails.',
      'Move or cancel an appointment and the reminders sort themselves out. Nobody has to remember to stop one.',
      'Patients get messages in their own language and their clinic’s local time.',
      'Confirmations, reminders and payment requests always go. Marketing only goes to people who agreed to it.',
      'Every marketing message carries an unsubscribe link, and what someone agreed to is recorded word for word.',
    ],
    audience: 'Any practice losing chairs to forgotten appointments.',
    roadmap: {
      'two-way': 'Two-way conversation, so a patient’s reply arrives in the app rather than on your phone.',
    },
    boundaries: [
      'Message costs are charged against a pre-funded balance, and campaigns stop when it runs out rather than running up a bill.',
    ],
    needs: [
      'Sending needs an SMS and email account connected first — we will help you set it up.',
    ],
  },
  recall: {
    title: 'Recall & reviews',
    summary: 'Bring patients back before they drift to someone else.',
    steps: [
      { title: 'It notices', body: 'A weekly sweep finds everyone with no appointment for longer than your recall interval.' },
      { title: 'It puts them in order', body: 'Most worth contacting first, so a limited budget is not spent alphabetically.' },
      { title: 'You reach out', body: 'Contact them from the app, or let the message go on its own.' },
    ],
    details: [
      'Patients who quietly stopped coming get noticed, instead of turning up at another practice in two years.',
      'The list is ordered by who is worth contacting, so a small messaging budget goes to the twenty patients that matter, not the first twenty alphabetically.',
      'You can see why a patient is near the top, so it is a judgement you can overrule rather than a number to trust.',
      'It stops when your messaging balance would run out, and tells you who it skipped — no surprise bill.',
      'Staff get a free in-app nudge; actually messaging the patient is your choice and charged.',
      'A review request goes out 48 hours after a completed appointment, while they still remember it fondly.',
      'The scoring runs on your own data inside the platform. Nothing about a patient is sent anywhere to work out who to contact first.',
    ],
    audience: 'Practices with enough patients that keeping track by memory stopped working a while ago.',
    boundaries: [
      'Recall messages are charged to your messaging balance, like any other patient message.',
    ],
  },
  'clinic-directory': {
    title: 'Directory & online booking',
    summary: 'Software that brings you patients, not just software that manages the ones you have.',
    steps: [
      { title: 'Publish your listing', body: 'Photos, doctors, services, opening hours, a pin on the map.' },
      { title: 'Get found', body: 'Patients search by city or by treatment, or browse the map.' },
      { title: 'They book themselves', body: 'Straight into your calendar, at midnight if that is when they are looking.' },
    ],
    details: [
      'A patient who has never heard of you can find you and book, without anyone at your desk being involved.',
      'They book at the hour they are actually thinking about it, which is rarely during your opening hours.',
      'A new location with no local reputation gets found on proximity and treatment rather than on word of mouth it has not earned yet.',
      'Your profile page is built to be understood by search engines, so it works outside our directory too.',
      'Someone with no account becomes a patient record from their name and phone at the moment they book.',
      'Ask for a deposit at booking if you use them, so online bookings are as committed as ones made by phone.',
      'Listings are not ranked or promoted, and there is no paid placement. What a patient sees is proximity and what they searched for, not who paid.',
    ],
    audience: 'Practices with chairs to fill, and new locations with no local reputation yet.',
    roadmap: {
      'reviews': 'Collecting and showing reviews on the directory. Today a review request is an email that points wherever you choose.',
    },
  },

  // ── Team & practice ────────────────────────────────────────────────────────
  'team-scheduling': {
    title: 'Team scheduling',
    summary: 'See next week’s gaps this week, while you can still do something about them.',
    steps: [
      { title: 'Say what you need', body: 'Chairs per location, and how many people you need on reception at once.' },
      { title: 'Staff enter their own hours', body: 'Doctors and reception put in their schedule and request time off themselves.' },
      { title: 'Fix the gaps early', body: 'The board shows where cover falls short, before it becomes Monday morning.' },
    ],
    details: [
      'You find out on Wednesday that Friday afternoon has one receptionist, rather than on Friday afternoon.',
      'Chairs with no doctor and shifts with nobody on reception are visible as gaps, not discovered as problems.',
      'A pending time-off request cannot disturb the live rota — the last approved version stays in force until someone decides.',
      'Contracted hours per person, so you can see who is short and who is heading for overtime.',
      'Need someone to cover Friday? A draft message is written for you, you edit it, and it goes out under your name.',
      'Replies come back as shifts you accept with one click, already trimmed to the real gap and checked against approved leave.',
      'The assistant never messages staff on its own. A person triggers it, reads it, and it goes out under their name.',
    ],
    audience: 'Practices with enough staff that coverage is a puzzle rather than a glance at a wall chart.',
    boundaries: [
      'Reception hours are tracked per clinic rather than per location.',
    ],
  },
  inventory: {
    title: 'Inventory',
    summary: 'Know what is in the cupboard before you need it, not while the patient is waiting.',
    steps: [
      { title: 'List what you hold', body: 'Per location, with the level at which you want warning and expiry where it matters.' },
      { title: 'It counts as you work', body: 'Recording a treatment takes out what it used. Nobody counts boxes on a Friday.' },
      { title: 'You get told', body: 'Daily checks for low stock and for anything about to expire.' },
    ],
    details: [
      'Running out mid-treatment stops being a thing that happens, because you were warned days before.',
      'Expired material is caught 30 days out, rather than found in a drawer during an inspection.',
      'Stock moves as you record treatments, so the figure on screen is not a guess from the last stocktake.',
      'Every change has a reason behind it — you can always see why a number is what it is.',
      'Stock can move between your locations, so one surgery being short does not mean an order.',
      'What you paid per item feeds the profitability report, so material cost in your margins is real, not estimated.',
    ],
    audience: 'Practices holding enough stock that running out mid-treatment is a real risk.',
    roadmap: {
      'price-library': 'A supplier price library to compare against, rather than only your own purchase history.',
      'purchase-orders': 'Raising purchase orders from the platform.',
    },
    boundaries: [
      'Deliveries come in from the supplier’s invoice rather than being typed in — see Supplier invoices.',
    ],
  },

  // ── Platform ───────────────────────────────────────────────────────────────
  'supplier-invoices': {
    title: 'Supplier invoices',
    summary: 'A delivery becomes stock in one pass, instead of an invoice nobody types in.',
    steps: [
      { title: 'Open the invoice', body: 'The supplier’s lines, with your matching stock items suggested beside them.' },
      { title: 'Confirm the matches', body: 'Say what each line is, or that it is not stock at all. You only do it once per product.' },
      { title: 'Receive it', body: 'Everything goes into stock at the right location, at the price you actually paid.' },
    ],
    details: [
      'Next month the same products from the same supplier arrive already matched — the work halves after the first invoice.',
      'Stock figures stop drifting, because deliveries stop being typed in by hand or skipped when it is busy.',
      'You receive at the price you actually paid, so your margins are built on real cost rather than last year’s.',
      'An invoice cannot be received twice, so a distracted afternoon cannot double your stock on paper.',
      'The same product can map to a different item at each location, because what you call things can differ by surgery.',
    ],
    audience: 'Practices whose stock figures have drifted because deliveries get typed in by hand, or not at all.',
    roadmap: {
      'efactura-pull': 'Pulling invoices in from e-Factura automatically, instead of working through them in the app.',
      'purchase-orders': 'Purchase orders, and matching an invoice against the order and the delivery.',
      'price-library': 'A price library to check a supplier’s figures against.',
    },
  },

  ai: {
    title: 'AI',
    summary: 'Stop typing notes. Know who will not turn up. Contact the right patients first.',
    steps: [
      { title: 'Dictate the visit', body: 'Say it out loud. It comes back as notes and filled-in fields you check before saving.' },
      { title: 'Just ask', body: 'Book, find and answer by typing or speaking, instead of clicking through screens.' },
      { title: 'It learns your practice', body: 'No-show prediction trains on your own patients, not on dentistry in general.' },
    ],
    details: [
      'Dictate the consultation instead of typing it, and spend the appointment looking at the patient.',
      'You are told which of tomorrow’s appointments are likely to be missed, in time to do something about it.',
      'The prediction learns from your patients and your history — not from a general model built on somebody else’s practice.',
      'Your recall list is put in order of who is worth contacting, so a small budget goes where it earns.',
      'Campaign copy gets drafted for you per audience; a person reads it and decides to send it.',
      'Nothing is applied automatically. Dictation, recommendations and drafts are all reviewed by a person before they count.',
      'Where an outside model is used, it never sees a patient’s name, ID or contact details.',
      'Dictation audio is deleted after seven days. The text stays as long as your retention policy says; the recording does not.',
    ],
    audience: 'Practices where the clinician spends the appointment typing instead of looking at the patient.',
    needs: [
      'The AI features have to be switched on for your clinic. Until they are they are simply off, and everything else works exactly the same.',
    ],
  },
  security: {
    title: 'Security & your data',
    summary: 'Answer the data-protection question confidently, and leave with everything if you ever want to.',
    steps: [
      { title: 'Your records are yours alone', body: 'Another clinic on the platform cannot reach them. That is enforced, not promised.' },
      { title: 'Consent is real', body: 'Not a ticked box — the exact wording someone agreed to, kept permanently.' },
      { title: 'You can walk away', body: 'Export everything, files included, whenever you like.' },
    ],
    details: [
      'When a patient asks what you hold on them, you can answer the same day rather than promising to look into it.',
      'A patient asking to be forgotten is a button: the person is anonymised and the clinical record the law makes you keep survives.',
      'You can leave with everything — records and the actual X-rays and documents, not a list of filenames. Nobody is held by their own data.',
      'Contact details and national ID are encrypted in the database, so a stolen copy is not a stolen patient list.',
      'Another clinic on the platform cannot see your records even by accident — the boundary is applied to every query automatically.',
      'Consent to reminders and to marketing is recorded with the words used and the moment it was given.',
      'Everything anyone does is written to a log that cannot be edited afterwards.',
      'Exports expire after 14 days and the archive is deleted when they do — a copy of everything you hold should not sit around indefinitely.',
    ],
    audience: 'Anyone who has to answer a data-protection question — which, for a dental practice, is everyone.',
    boundaries: [
      'A full export is built in the background — the more you hold, the longer it takes to build.',
    ],
  },
  integrations: {
    title: 'Integrations & API',
    summary: 'Your appointments in the calendar you already use, and a way to plug in whatever else you run.',
    steps: [
      { title: 'Connect a calendar', body: 'A doctor links Google or Outlook once. Their clinic appointments appear there from then on.' },
      { title: 'Their own life protects the diary', body: 'Busy time from their personal calendar blocks bookings here — no more double-booked Fridays.' },
      { title: 'Build on it if you want', body: 'Webhooks and a documented API for your accountant’s system or your own script.' },
    ],
    details: [
      'A doctor sees their clinic day in the calendar already on their phone, without checking two places.',
      'An outside commitment in their own calendar blocks the slot here, so nobody is booked against their child’s school play.',
      'The calendar feed carries no patient details — an appointment shows as an appointment, which is what a shared phone screen should show.',
      'Your other systems can be told when something happens rather than polled, and the messages are signed so they can be trusted.',
      'A failing webhook is retried three times and then switched off, rather than hammering a dead endpoint forever.',
      'API keys are scoped to what they need and stored so they cannot be read back out — including by us.',
    ],
    audience: 'Practices with an accountant’s system, a marketing tool or an in-house script to feed.',
    roadmap: {
      'accounting': 'Ready-made connections to the usual accounting packages, so nobody has to build one.',
    },
    needs: [
      'Calendar sync has to be switched on for your clinic, and then each doctor connects their own calendar.',
    ],
  },
};
